/** The slice of `LoroText` this helper needs — structural, so the admin app
 * needs no direct `loro-crdt` dependency for the type. */
export interface EditableText {
  delete(pos: number, len: number): void
  insert(pos: number, text: string): void
}

/**
 * Applies the common-prefix/suffix difference between two strings as a real
 * CRDT insert/delete, so a textarea bound to a LoroText merges with concurrent
 * remote edits instead of clobbering the whole container on every keystroke.
 */
export const applyTextDiff = (
  text: EditableText,
  oldStr: string,
  newStr: string,
): void => {
  let start = 0
  while (
    start < oldStr.length &&
    start < newStr.length &&
    oldStr[start] === newStr[start]
  )
    start++
  let oldEnd = oldStr.length
  let newEnd = newStr.length
  while (
    oldEnd > start &&
    newEnd > start &&
    oldStr[oldEnd - 1] === newStr[newEnd - 1]
  ) {
    oldEnd--
    newEnd--
  }
  if (oldEnd > start) text.delete(start, oldEnd - start)
  if (newEnd > start) text.insert(start, newStr.slice(start, newEnd))
}

/** Opaque Loro `Cursor`; only ever handed back to `CursorResolver.getCursorPos`. */
export type TextCursor = object

/** The slice of `LoroText` needed to anchor a selection to CRDT positions. */
export interface AnchorableText extends EditableText {
  getCursor(pos: number, side?: -1 | 0 | 1): TextCursor | undefined
}

/** The slice of `LoroDoc` needed to resolve an anchored selection back to offsets. */
export interface CursorResolver {
  getCursorPos(cursor: TextCursor): { offset: number } | undefined
}

export interface CaretAnchor {
  start?: TextCursor
  end?: TextCursor
}

/**
 * Anchors a selection to CRDT cursor positions so it survives concurrent
 * remote edits — an offset alone drifts the moment the document changes
 * upstream of it.
 */
export const anchorCaret = (
  text: AnchorableText,
  start: number,
  end: number,
): CaretAnchor => ({
  start: text.getCursor(start, 0),
  end: end === start ? undefined : text.getCursor(end, 0),
})

/**
 * Resolves an anchored selection against the current document, clamped to
 * `length`. Falls back to `fallback`'s offset for either edge whose cursor no
 * longer resolves (the anchored character was deleted).
 */
export const resolveCaret = (
  doc: CursorResolver,
  anchor: CaretAnchor,
  fallback: { start: number; end: number },
  length: number,
): { start: number; end: number } => {
  const clamp = (n: number): number => Math.min(Math.max(n, 0), length)
  const startOffset =
    (anchor.start && doc.getCursorPos(anchor.start)?.offset) ?? fallback.start
  const start = clamp(startOffset)
  // No distinct end anchor means the selection was collapsed at anchor time
  // — keep it collapsed by reusing the resolved start.
  const endOffset = anchor.end
    ? (doc.getCursorPos(anchor.end)?.offset ?? fallback.end)
    : startOffset
  const end = clamp(Math.max(endOffset, start))
  return { start, end }
}
